// Confluence Engine — scores every setup 0–10 and assigns A+/A/B/C/D grade
// Also serves as Level 4 Thinking Loop: auto-loads ICT storage data for quick scoring

import { getICT, getRules, getBehaviors } from '../services/storage';
import { getCurrentSession, getDayOfWeek } from '../utils/dateUtils';
import { getLatestSignalSnapshot } from '../services/scriptAnalysis';

// ── Factor definitions ─────────────────────────────────────────────────
// weight = contribution to final score (normalized to 10 at the end)
export const CONFLUENCE_FACTORS = {
  // ICT Core (heavy weight)
  htf_bias:        { weight: 2.0, label: 'HTF Bias Aligned (D1/H4 direction confirmed)',       group: 'ICT', icon: '📊' },
  liquidity_swept: { weight: 2.0, label: 'Liquidity Swept Before Entry (SSL/BSL hunted)',       group: 'ICT', icon: '💧' },
  ob_present:      { weight: 2.0, label: 'Order Block at Entry Level',                          group: 'ICT', icon: '🏛️' },
  choch_seen:      { weight: 2.0, label: 'CHoCH Confirmed (Change of Character)',               group: 'ICT', icon: '🔄' },

  // Fibonacci (heavy weight) — fib_ote is auto-detected from A_Signal.js's
  // real OTE zone check (0.618-0.79, the validated retracement), not a
  // guess. "sir_macro_zone" (old 0.15/0.75 weekly levels) was REMOVED — it
  // used hardcoded, stale swing prices ($3113-$5866) that never updated as
  // gold's real price moved, and those levels were never backtested anyway
  // (see FibCalculator.jsx's "जुने levels — reference only" relabel).
  fib_ote:         { weight: 2.0, label: 'At Validated OTE Zone (0.618-0.79 retracement — auto from A_Signal.js)', group: 'FIB', icon: '📐' },
  fib_382:         { weight: 1.0, label: 'At 0.382 Fibonacci (entry-level confluence)',         group: 'FIB', icon: '📏' },

  // ICT Support — bos_confirmed/choch_seen are auto-detected from
  // A_Structure.js's real close-confirmed swing detection.
  fvg_present:     { weight: 1.5, label: 'Fair Value Gap Present at Level',                     group: 'ICT', icon: '⬜' },
  bos_confirmed:   { weight: 1.5, label: 'BOS Confirmed (auto from A_Structure.js)',             group: 'ICT', icon: '✅' },

  // SMC + OB Fib 0.5 Strategy — UNVALIDATED (jarvisDNA.js explicitly marks
  // this Pine script as "no working entry/SL logic at all, do NOT use for
  // live decisions"). Kept as optional manual factors, low default trust —
  // never auto-checked, always require Sir's own judgment.
  ob_fib_05:       { weight: 2.0, label: '⚠️ At OB Fib 0.5 Zone (जुनी SMC strategy — validated नाही, manual only)', group: 'SMC', icon: '🔵' },
  ob_unmitigated:  { weight: 1.5, label: '⚠️ Order Block Unmitigated (जुनी SMC strategy — validated नाही, manual only)', group: 'SMC', icon: '🟢' },
  internal_bos:    { weight: 1.0, label: '⚠️ Internal BOS (जुनी SMC strategy — validated नाही, manual only)',   group: 'SMC', icon: '🔺' },

  // Context
  session_prime:   { weight: 1.0, label: 'Prime Session Active (London 09–14 / NY 14–20 IST)', group: 'CONTEXT', icon: '🕐' },
  news_clear:      { weight: 1.0, label: 'No High-Impact News Within 30 Minutes',              group: 'CONTEXT', icon: '📰' },
  dxy_confirms:    { weight: 1.0, label: 'DXY Confirming Direction (inverse correlation)',      group: 'CONTEXT', icon: '💵' },
  pattern_match:   { weight: 1.0, label: 'Historical Pattern Match in JARVIS Memory',          group: 'CONTEXT', icon: '🧠' },
  psychology_good: { weight: 0.5, label: 'Psychology Rating ≥ 7/10 (mental clarity)',          group: 'CONTEXT', icon: '🧘' },
};

const MAX_WEIGHT = Object.values(CONFLUENCE_FACTORS).reduce((sum, f) => sum + f.weight, 0);

// ── Grade thresholds ────────────────────────────────────────────────────
export const GRADE_CONFIG = {
  'A+': { min: 8.0, color: '#00ff88', bg: 'rgba(0,255,136,0.12)', action: 'TRADE — Full size',     winRate: '80–90%', emoji: '🔥' },
  'A':  { min: 6.5, color: '#00d4ff', bg: 'rgba(0,212,255,0.10)', action: 'TRADE — Normal size',   winRate: '70–80%', emoji: '✅' },
  'B':  { min: 5.0, color: '#ffaa00', bg: 'rgba(255,170,0,0.10)', action: 'CAUTION — Half size',   winRate: '55–70%', emoji: '⚡' },
  'C':  { min: 3.0, color: '#ff6600', bg: 'rgba(255,102,0,0.10)', action: 'SKIP — Not worth it',   winRate: '45–55%', emoji: '⚠️' },
  'D':  { min: 0,   color: '#ff3333', bg: 'rgba(255,51,51,0.10)', action: 'NO TRADE — Walk away',  winRate: '<45%',   emoji: '❌' },
};

export const calculateConfluence = (activeFactors) => {
  let rawScore = 0;
  const matched = [];
  const missing = [];

  Object.entries(CONFLUENCE_FACTORS).forEach(([key, factor]) => {
    if (activeFactors[key]) {
      rawScore += factor.weight;
      matched.push({ key, ...factor });
    } else {
      missing.push({ key, ...factor });
    }
  });

  const score = parseFloat(((rawScore / MAX_WEIGHT) * 10).toFixed(1));
  const grade = score >= 8.0 ? 'A+' : score >= 6.5 ? 'A' : score >= 5.0 ? 'B' : score >= 3.0 ? 'C' : 'D';
  const config = GRADE_CONFIG[grade];

  // Top missing factors sorted by weight (most impactful first)
  const topMissing = [...missing].sort((a, b) => b.weight - a.weight).slice(0, 4);

  // What would upgrade the grade
  let upgradeHint = null;
  const nextGrade = grade === 'D' ? 'C' : grade === 'C' ? 'B' : grade === 'B' ? 'A' : grade === 'A' ? 'A+' : null;
  if (nextGrade) {
    const needed = GRADE_CONFIG[nextGrade].min - score;
    const topFactor = topMissing[0];
    upgradeHint = topFactor
      ? `Add ${topFactor.label.split('(')[0].trim()} (+${topFactor.weight.toFixed(1)}) to reach ${nextGrade} — need ${needed.toFixed(1)} more points`
      : null;
  }

  return {
    score, grade, config,
    rawScore: rawScore.toFixed(2),
    maxPossible: MAX_WEIGHT.toFixed(2),
    matched, missing, topMissing,
    shouldTrade: score >= 6.5,
    upgradeHint,
    factorCount: matched.length,
    totalFactors: Object.keys(CONFLUENCE_FACTORS).length,
  };
};

// ── Auto-load from stored ICT data ──────────────────────────────────────
export const autoLoadFromStorage = (currentPrice) => {
  const ict       = getICT();
  const session   = getCurrentSession();
  const rules     = getRules().filter(r => r.status === 'active');
  const behaviors = getBehaviors();
  const p         = parseFloat(currentPrice) || 0;

  const activeOBs = (ict.order_blocks    || []).filter(o => o.status === 'Active');
  const openFVGs  = (ict.fvg             || []).filter(f => f.status === 'Open');
  const recentMS  = (ict.market_structure || []).slice(-3);

  // Price-range proximity (within 15 USD = "at the level")
  const PROXIMITY = 15;
  const nearOB  = p > 0 && activeOBs.some(ob  => p >= (ob.bottom - PROXIMITY) && p <= (ob.top + PROXIMITY));
  const nearFVG = p > 0 && openFVGs.some(fvg  => p >= (fvg.bottom - PROXIMITY) && p <= (fvg.top + PROXIMITY));

  // OB Fib 0.5 Zone: price is in the lower half of a bullish OB (below midpoint) or upper half of bearish OB (above midpoint)
  // Midpoint = (OB top + OB bottom) / 2
  const nearOBFib05 = p > 0 && activeOBs.some(ob => {
    const mid = (ob.top + ob.bottom) / 2;
    if (ob.type === 'Bullish' || ob.direction === 'BUY' || ob.direction === 'bullish') {
      // BUY zone = below midpoint (lower half of bullish OB)
      return p >= (ob.bottom - PROXIMITY) && p <= (mid + PROXIMITY);
    }
    if (ob.type === 'Bearish' || ob.direction === 'SELL' || ob.direction === 'bearish') {
      // SELL zone = above midpoint (upper half of bearish OB)
      return p >= (mid - PROXIMITY) && p <= (ob.top + PROXIMITY);
    }
    return false;
  });

  // Unmitigated OBs: OBs that have 'unmitigated' flag OR no mitigated_at date
  const hasUnmitigatedOB = p > 0 && activeOBs.some(ob => {
    const unmitigated = ob.unmitigated === true || (!ob.mitigated_at && ob.status === 'Active');
    const nearby = p >= (ob.bottom - PROXIMITY) && p <= (ob.top + PROXIMITY);
    return unmitigated && nearby;
  });

  // Internal BOS = fast structure shift (recent MS event within last 5 bars labeled as InternalBOS or BOS_internal)
  const recentInternalBOS = recentMS.some(ms =>
    ms.event === 'InternalBOS' || ms.event === 'BOS_internal' || ms.event === 'iBOS'
  );

  // BOS/CHoCH from recent market structure — now includes entries
  // auto-logged by scriptAnalysis.js's autoLogStructureToICT() from real
  // A_Structure.js output, alongside anything Sir types in manually.
  const recentBOS   = recentMS.some(ms => ms.event === 'BOS');
  const recentCHoCH = recentMS.some(ms => ms.event === 'CHoCH');

  // Session check
  const primeSession = ['London', 'London+NY'].includes(session.name);

  // Pattern/behavior match check
  const hasPatterns = behaviors.length >= 3 || rules.length >= 2;

  // Real gate status from the last A_Signal.js check (any validated TF,
  // most recent) — this is what makes fib_ote/htf_bias auto-checked
  // instead of always defaulting to false pending manual confirmation.
  const snap = getLatestSignalSnapshot();
  const snapFresh = snap && (Date.now() - snap.ts) < 30 * 60 * 1000; // within 30 min — stale otherwise

  return {
    // Auto-detected from storage — user can still override
    ob_present:      nearOB,
    fvg_present:     nearFVG,
    bos_confirmed:   recentBOS,
    choch_seen:      recentCHoCH,
    session_prime:   primeSession,
    pattern_match:   hasPatterns,
    // SMC + OB Fib 0.5 — auto-detected, BUT this whole strategy is
    // unvalidated (see label warnings above) — shown, not recommended.
    ob_fib_05:       nearOBFib05,
    ob_unmitigated:  hasUnmitigatedOB,
    internal_bos:    recentInternalBOS,
    // Real, auto-detected from A_Signal.js (only if checked within the
    // last 30 min — otherwise falls back to manual, same as before)
    fib_ote:         snapFresh ? snap.fibInZone   : false,
    htf_bias:        snapFresh ? snap.htfAligned  : false,
    // Still require manual confirmation — nothing in A_Signal.js computes these
    liquidity_swept: false,
    fib_382:         false,
    news_clear:      false,
    dxy_confirms:    false,
    psychology_good: false,
  };
};

// ── Session + day-of-week context ───────────────────────────────────────
export const getSetupContext = () => {
  const session = getCurrentSession();
  const day     = getDayOfWeek();
  const hour    = parseInt(new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false }));

  const sessionTip = session.name === 'London'    ? 'Best session for manipulation + distribution. High probability.'
                   : session.name === 'London+NY' ? 'Continuation session. Strong moves. Good for trend trades.'
                   : session.name === 'Asian'      ? 'Low volume. Range building. Avoid directional trades.'
                   : session.name === 'NY Close'   ? 'Institutional rebalancing. Reversals possible.'
                   : 'Low-quality session. Only A+ setups worth considering.';

  const dayTip = day === 'Monday'    ? 'Monday: Slow start. Wait for direction to confirm.'
               : day === 'Tuesday'   ? 'Tuesday: Often strong AMD pattern. Watch for Asian sweep → London rally.'
               : day === 'Wednesday' ? 'Wednesday: High volatility day. News risk. Be careful.'
               : day === 'Thursday'  ? 'Thursday: Good trend continuation day.'
               : day === 'Friday'    ? 'Friday: Close positions before weekend. Reduce size.'
               : 'Weekend: Market closed or very low liquidity.';

  return { session, day, hour, sessionTip, dayTip };
};
